/**
 * Import / export crawlers as portable .dccchar text, wrapping the vendored
 * portable layer (packages/dcc-rules/src/portable). This is the same file format the
 * desktop app produces, so a crawler exported there imports here and vice
 * versa — and it's the substrate the later Drive backup will reuse.
 *
 * Scope note: the extension builds crawlers from CORE content, so custom
 * content travels empty. buildPortableExport still runs the dependency check
 * and would refuse an export that referenced missing custom definitions, so
 * this stays honest rather than shipping a broken package.
 */
import type { Character } from '@dcc/rules/domain/character.ts';
import {
  buildPortableExport,
  serializePortableFile,
  suggestedFileName,
  type ExportOptions,
} from '@dcc/rules/portable/exportCharacter.ts';
import { parsePortableFile } from '@dcc/rules/portable/importCharacter.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';

/** Catalogue-backed source for the export dependency check. */
const catalogueSource = {
  findSkill: (id: string) => contentRegistry.getSkill(id),
  findAttack: (id: string) => contentRegistry.getAttack(id),
  findBackground: (id: string) => contentRegistry.getBackground(id),
  findItem: (id: string) => contentRegistry.getItem(id),
};

function exportOptions(): ExportOptions {
  return { rulesetVersion: 'dcc-core', appVersion: 'obr-0.1.0', exportedAt: new Date().toISOString() };
}

export type ExportResult =
  | { ok: true; text: string; fileName: string }
  | { ok: false; message: string };

/** Serialize a crawler to portable .dccchar text. */
export function exportCrawler(character: Character): ExportResult {
  const built = buildPortableExport(character, exportOptions(), catalogueSource);
  if (!built.ok) return { ok: false, message: built.message };
  return {
    ok: true,
    text: serializePortableFile(built.file),
    fileName: suggestedFileName(character),
  };
}

export type ImportResult =
  | { ok: true; character: Character }
  | { ok: false; errors: string[] };

/**
 * Parse portable text back into a Character. Known skill ids come from the
 * core catalogue so the parser can tell core references from custom ones.
 */
export function importCrawler(text: string): ImportResult {
  const knownSkillIds = new Set(contentRegistry.allSkills().map((s) => s.id));
  const outcome = parsePortableFile(text, knownSkillIds);
  if (!outcome.ok) return { ok: false, errors: outcome.errors };
  return { ok: true, character: outcome.file.character };
}
